import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

export interface AIPlannerState {
  // Whether the global planner panel is showing.
  isPanelOpen: boolean;
  // The coach speaks first as soon as the conversation is known to be empty.
  isKickoffPending: boolean;
  // Whether the conversation has left onboarding, as the route reports it. The app reads
  // this to leave its focused onboarding mode. Set once per session.
  onboardingExited: boolean;
  // What caused the exit, for analytics and for UI that explains it.
  onboardingExit: { settled: string[]; asked: string[]; at: number } | null;
  // Coach replies that finished while the panel was closed. Cleared when it opens.
  unreadReplies: number;
  // Whether the desktop panel covers the whole screen instead of docking. Cleared on close.
  isPanelExpanded: boolean;
  // Whether the panel was left open in an earlier visit. It then shows without its opening
  // animation. Cleared when it closes.
  isPanelRestored: boolean;
  // Whether the couple turned the planner off in settings, as saved on the wedding.
  isDisabledByCouple: boolean;
  // Whether the onboarding screen owns the panel. While it does, the panel has no header
  // and cannot close. Cleared when that screen goes away.
  isOnboardingActive: boolean;
}

const initialState: AIPlannerState = {
  isPanelOpen: false,
  isKickoffPending: false,
  onboardingExited: false,
  onboardingExit: null,
  unreadReplies: 0,
  isPanelExpanded: false,
  isPanelRestored: false,
  isDisabledByCouple: false,
  isOnboardingActive: false,
};

const aiPlannerSlice = createSlice({
  name: 'aiPlanner',
  initialState,
  reducers: {
    setAIPlannerPanelOpen: (state, action: PayloadAction<boolean>) => {
      if (!action.payload && state.isOnboardingActive) return;
      state.isPanelOpen = action.payload;
      if (action.payload) state.unreadReplies = 0;
      else {
        state.isPanelExpanded = false;
        state.isPanelRestored = false;
      }
    },
    openAIPlanner: (state) => {
      state.isPanelOpen = true;
      state.unreadReplies = 0;
    },
    // Reopens the panel left open in an earlier visit
    restoreAIPlanner: (state) => {
      state.isPanelOpen = true;
      state.unreadReplies = 0;
      state.isPanelRestored = true;
    },
    // Opens the panel in place for the onboarding screen, with the coach speaking first
    startAIPlannerOnboarding: (state) => {
      state.isOnboardingActive = true;
      state.isPanelOpen = true;
      state.isPanelRestored = true;
      state.isPanelExpanded = false;
      state.isKickoffPending = true;
      state.unreadReplies = 0;
    },
    endAIPlannerOnboarding: (state) => {
      state.isOnboardingActive = false;
    },
    closeAIPlanner: (state) => {
      if (state.isOnboardingActive) return;
      state.isPanelOpen = false;
      state.isPanelExpanded = false;
      state.isPanelRestored = false;
    },
    toggleAIPlanner: (state) => {
      if (state.isOnboardingActive) return;
      state.isPanelOpen = !state.isPanelOpen;
      if (state.isPanelOpen) state.unreadReplies = 0;
      else {
        state.isPanelExpanded = false;
        state.isPanelRestored = false;
      }
    },
    toggleAIPlannerExpanded: (state) => {
      state.isPanelExpanded = !state.isPanelExpanded;
    },
    // Counted only while the panel is closed, as an open panel shows the reply itself.
    plannerReplyFinished: (state) => {
      if (!state.isPanelOpen) state.unreadReplies += 1;
    },
    setAIPlannerKickoffPending: (state, action: PayloadAction<boolean>) => {
      state.isKickoffPending = action.payload;
    },
    // Turned off or back on in settings. Applied at once, the save follows.
    setAIPlannerDisabled: (state, action: PayloadAction<boolean>) => {
      state.isDisabledByCouple = action.payload;
    },
    // The saved choice, read from the wedding's planner agent
    aiPlannerSettingsLoaded: (state, action: PayloadAction<{ isDisabled: boolean }>) => {
      state.isDisabledByCouple = action.payload.isDisabled;
    },
    // The signal the rest of the app listens for to leave the onboarding mode.
    onboardingExited: (
      state,
      action: PayloadAction<{ settled: string[]; asked: string[]; at: number }>,
    ) => {
      state.onboardingExited = true;
      state.onboardingExit = action.payload;
    },
  },
});

export const {
  setAIPlannerPanelOpen,
  openAIPlanner,
  restoreAIPlanner,
  startAIPlannerOnboarding,
  endAIPlannerOnboarding,
  closeAIPlanner,
  toggleAIPlanner,
  toggleAIPlannerExpanded,
  setAIPlannerKickoffPending,
  onboardingExited,
  plannerReplyFinished,
  setAIPlannerDisabled,
  aiPlannerSettingsLoaded,
} = aiPlannerSlice.actions;

export default aiPlannerSlice.reducer;
